import React, { useState, useEffect } from 'react';
import { Modal } from '../ui';
import { Button } from '../ui';
import { Badge } from '../ui';
import { abacUserAttributeService, abacResourceAttributeService, abacUserService, abacResourceService } from '../../services/abac';
import { useLanguage } from '../../state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { DataItem } from '../../types/crud';

interface Attribute {
  id: string;
  attribute_name: string;
  data_type: string;
  description: string;
  is_active: boolean;
}

interface AttributeAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'USER' | 'RESOURCE';
  entity: DataItem;
  onAttributeAssigned?: () => void;
}

const AttributeAssignmentModal: React.FC<AttributeAssignmentModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entity,
  onAttributeAssigned
}) => {
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<Attribute | null>(null);
  const [attributeValues, setAttributeValues] = useState<string[]>(['']);
  const [assigning, setAssigning] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen) {
      fetchAttributes();
    }
  }, [isOpen, entityType]);

  const fetchAttributes = async () => {
    try {
      setLoading(true);
      const service = entityType === 'USER' ? abacUserAttributeService : abacResourceAttributeService;
      const response = await service.getAll();
      // Debug statements removed for cleaner console output
      
      // Handle different response structures
      let attributesData = [];
      if (response && response.data) {
        attributesData = response.data;
      } else if (Array.isArray(response)) {
        attributesData = response;
      } else if (response && Array.isArray(response.attributes)) {
        attributesData = response.attributes;
      }
      
            setAttributes(attributesData);
    } catch (error) {
      console.error('Failed to fetch attributes:', error);
      toast.error(t('abac.failedToFetchAttributes'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAttribute = (attribute: Attribute) => {
    setSelectedAttribute(attribute);
    setAttributeValues(['']);
  };

  const addValue = () => {
    setAttributeValues([...attributeValues, '']);
  };

  const removeValue = (index: number) => {
    if (attributeValues.length > 1) {
      const newValues = attributeValues.filter((_, i) => i !== index);
      setAttributeValues(newValues);
    }
  };

  const updateValue = (index: number, value: string) => {
    const newValues = [...attributeValues];
    newValues[index] = value;
    setAttributeValues(newValues);
  };

  const handleSaveAssignment = async () => {
    if (!selectedAttribute) {
      toast.error(t('abac.pleaseEnterAttributeValue'));
      return;
    }

    // Filter out empty values
    const validValues = attributeValues.filter(value => value.trim() !== '');
    
    if (validValues.length === 0) {
      toast.error(t('abac.pleaseEnterAttributeValue'));
      return;
    }

    try {
      setAssigning(true);
      const service = entityType === 'USER' ? abacUserService : abacResourceService;
      
      await service.assignAttributeValues({
        entity_type: entityType,
        entity_id: entity.id,
        attribute_id: selectedAttribute.id,
        values: validValues
      });

      toast.success(t('abac.attributeValueAssigned'));
      setSelectedAttribute(null);
      setAttributeValues(['']);
      onAttributeAssigned?.();
    } catch (error) {
      console.error('Failed to assign attribute:', error);
      toast.error(t('abac.failedToAssignAttributeValue'));
    } finally {
      setAssigning(false);
    }
  };

  const getEntityName = () => {
    if (entityType === 'USER') {
      return `${entity.first_name} ${entity.last_name}`;
    }
    return entity.name || entity.title || entity.id;
  };

  const renderValueInput = (index: number) => {
    if (!selectedAttribute) return null;

    switch (selectedAttribute.data_type) {
      case 'STRING':
        return (
          <input
            type="text"
            value={attributeValues[index]}
            onChange={(e) => updateValue(index, e.target.value)}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
            placeholder={t('abac.enterStringValue')}
          />
        );
      case 'NUMBER':
        return (
          <input
            type="number"
            value={attributeValues[index]}
            onChange={(e) => updateValue(index, e.target.value)}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
            placeholder={t('abac.enterNumberValue')}
          />
        );
      case 'BOOLEAN':
        return (
          <select
            value={attributeValues[index]}
            onChange={(e) => updateValue(index, e.target.value)}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
          >
            <option value="">{t('abac.selectBooleanValue')}</option>
            <option value="true">{t('common.yes')}</option>
            <option value="false">{t('common.no')}</option>
          </select>
        );
      case 'DATE':
        return (
          <input
            type="date"
            value={attributeValues[index]}
            onChange={(e) => updateValue(index, e.target.value)}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
          />
        );
      default:
        return (
          <input
            type="text"
            value={attributeValues[index]}
            onChange={(e) => updateValue(index, e.target.value)}
            className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
            placeholder={t('abac.enterValue')}
          />
        );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('abac.assignAttributeTo', { 
        entityName: getEntityName(),
        entityType: entityType === 'USER' ? t('common.user') : t('common.resource')
      })}
      size="lg"
    >
      <div className="space-y-6">
        {/* Available Attributes */}
        <div>
          <h4 className="text-sm font-medium text-[var(--color-secondary-text)] mb-3">
            {t('abac.availableAttributes')} ({attributes.length})
          </h4>
          {loading ? (
            <p className="text-[var(--color-secondary-text)] opacity-75">{t('common.loading')}...</p>
          ) : attributes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {attributes.map((attribute) => (
                <div
                  key={attribute.id}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                    selectedAttribute?.id === attribute.id
                      ? 'border-[var(--color-info)] bg-[var(--color-info)]/10'
                      : 'border-[var(--color-border)] hover:border-[var(--color-border)]/80'
                  }`}
                  onClick={() => handleAssignAttribute(attribute)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-[var(--color-surface-text)]">{attribute.attribute_name}</span>
                    <Badge variant="outline" className="text-xs">
                      {attribute.data_type}
                    </Badge>
                  </div>
                  {attribute.description && (
                    <p className="text-sm text-[var(--color-secondary-text)]">{attribute.description}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">{t('abac.noAttributesAvailable')}</p>
          )}
        </div>

        {/* Attribute Value Assignment */}
        {selectedAttribute && (
          <div className="border-t pt-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                  {t('abac.attributeValue')} - {selectedAttribute.attribute_name}
                </label>
                <p className="text-sm text-[var(--color-secondary-text)] mb-3">
                  {t('abac.enterMultipleValues')}
                </p>
                
                {/* Multiple Value Inputs */}
                <div className="space-y-3">
                  {attributeValues.map((_value, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="flex-1">
                        {renderValueInput(index)}
                      </div>
                      {attributeValues.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeValue(index)}
                          className="px-3 py-2 text-[var(--color-error)] hover:opacity-80 hover:bg-[var(--color-error)]/10 rounded-md transition-colors"
                          disabled={assigning}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Add Value Button */}
                <button
                  type="button"
                  onClick={addValue}
                  className="mt-3 px-4 py-2 text-sm text-[var(--color-info)] hover:opacity-80 hover:bg-[var(--color-info)]/10 rounded-md transition-colors border border-[var(--color-info)]/30"
                  disabled={assigning}
                >
                  + {t('abac.addAnotherValue')}
                </button>
              </div>
              
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedAttribute(null);
                    setAttributeValues(['']);
                  }}
                  disabled={assigning}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleSaveAssignment}
                  disabled={attributeValues.every(value => value.trim() === '') || assigning}
                  loading={assigning}
                >
                  {t('abac.assignAttribute')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AttributeAssignmentModal;
